"use client";

import { useEffect, useId, useRef, useState } from "react";
import Link from "next/link";
import { AlertTriangle, Eye, EyeOff, Loader2, Play, Send } from "lucide-react";
import { API_KEY_LENGTH, API_KEY_PREFIX, isWellFormedApiKey } from "../../lib/keyFormat";
import { errorAnchor } from "../anchors";
import { buildRequestPath } from "../snippets";
import type { DocsParam } from "../types";
import { useApiKey } from "./ApiKeyContext";
import { CodeBlock } from "./CodeBlock";
import { C } from "./RichText";
import { LINK, MONO, formatCount, unitsLabel } from "./styles";

/** What the panel needs from an endpoint (kept small: it is serialized into the page). */
export type TryItEndpoint = {
  id: string;
  /** Path template, e.g. "/api/v1/games/{appid}/players". */
  path: string;
  units: number;
  params: Pick<DocsParam, "name" | "in" | "required" | "type" | "enumValues" | "defaultValue">[];
  /** Starting values: the endpoint's example call. */
  exampleParams: Record<string, string>;
  dashboardPath: string;
};

const STATUS_TEXT: Record<number, string> = {
  200: "OK",
  400: "Bad Request",
  401: "Unauthorized",
  403: "Forbidden",
  404: "Not Found",
  405: "Method Not Allowed",
  429: "Too Many Requests",
  500: "Internal Server Error",
  502: "Bad Gateway",
  503: "Service Unavailable",
  504: "Gateway Timeout",
};

/** "spk_ followed by 38 letters and digits", from the key format itself. */
const KEY_SHAPE = `${API_KEY_PREFIX} followed by ${API_KEY_LENGTH - API_KEY_PREFIX.length} letters and digits`;

const SHOWN_HEADERS = [
  "x-request-id",
  "x-units-charged",
  "x-units-remaining-day",
  "x-units-remaining-month",
  "x-ratelimit-remaining",
  "retry-after",
] as const;

type Result = {
  status: number;
  statusText: string;
  ms: number;
  headers: Partial<Record<(typeof SHOWN_HEADERS)[number], string>>;
  body: string;
  isJson: boolean;
  errorCode: string | null;
};

function readBody(text: string): { body: string; isJson: boolean; errorCode: string | null } {
  try {
    const parsed: unknown = JSON.parse(text);
    const error = parsed && typeof parsed === "object" ? (parsed as { error?: { code?: unknown } }).error : undefined;
    return {
      body: JSON.stringify(parsed, null, 2),
      isJson: true,
      errorCode: typeof error?.code === "string" ? error.code : null,
    };
  } catch {
    return { body: text, isJson: false, errorCode: null };
  }
}

function statusTone(status: number): string {
  if (status >= 200 && status < 300) return "border-[#a4d007]/50 bg-[#a4d007]/10 text-[#c6ec4a]";
  if (status >= 500) return "border-red-400/50 bg-red-500/10 text-red-200";
  return "border-[#e35e1c]/50 bg-[#e35e1c]/10 text-[#f0a37c]";
}

function numberOrDash(v: string | undefined): string {
  if (v === undefined || v === "" || !Number.isFinite(Number(v))) return "—";
  return formatCount(Number(v));
}

/**
 * Sends one real request to the same origin's /api/v1 with the visitor's
 * key, and shows the status, what it cost and the body. The key goes in the
 * Authorization header, never the URL.
 */
export function TryItPanel({ endpoint }: { endpoint: TryItEndpoint }) {
  const { apiKey, setApiKey, remember, setRemember } = useApiKey();
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState<Record<string, string>>(() => ({ ...endpoint.exampleParams }));
  const [showKey, setShowKey] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [result, setResult] = useState<Result | null>(null);
  const inFlight = useRef<AbortController | null>(null);
  const id = useId();

  useEffect(() => () => inFlight.current?.abort(), []);

  const { path, missing } = buildRequestPath(endpoint.path, endpoint.params, values);
  const trimmedKey = apiKey.trim();
  const keyLooksWrong = trimmedKey !== "" && !isWellFormedApiKey(trimmedKey);
  const cost = endpoint.units === 0 ? "This call is free." : `A successful call costs ${unitsLabel(endpoint.units)}; errors are free.`;

  async function send() {
    if (trimmedKey === "") {
      setProblem("Paste your API key first.");
      return;
    }
    if (!isWellFormedApiKey(trimmedKey)) {
      setProblem(`That isn't a steampeek API key: keys are ${KEY_SHAPE}.`);
      return;
    }
    if (missing.length > 0) {
      setProblem(`Fill in ${missing.join(", ")}.`);
      return;
    }
    setProblem(null);
    inFlight.current?.abort();
    const controller = new AbortController();
    inFlight.current = controller;
    setPending(true);
    const started = performance.now();
    try {
      const res = await fetch(path, {
        method: "GET",
        headers: { Authorization: `Bearer ${trimmedKey}`, Accept: "application/json" },
        cache: "no-store",
        signal: controller.signal,
      });
      const text = await res.text();
      if (controller.signal.aborted) return;
      const headers: Result["headers"] = {};
      for (const name of SHOWN_HEADERS) {
        const v = res.headers.get(name);
        if (v !== null) headers[name] = v;
      }
      setResult({
        status: res.status,
        statusText: res.statusText || STATUS_TEXT[res.status] || "",
        ms: Math.round(performance.now() - started),
        headers,
        ...readBody(text),
      });
    } catch (err) {
      if (controller.signal.aborted) return;
      setResult(null);
      setProblem(`The request didn't complete: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      if (inFlight.current === controller) {
        inFlight.current = null;
        setPending(false);
      }
    }
  }

  const panelId = `${id}-panel`;
  const fieldClass = `${MONO} w-full min-w-0 rounded-md border border-[#2a475e] bg-[#0e1620] px-2.5 py-1.5 text-[13px] text-[#e8eef3] placeholder:text-[#5f6b75] focus:border-[#66c0f4] focus:outline-none`;

  return (
    <div className="rounded-lg border border-[#2a475e] bg-[#16202d]">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full flex-wrap items-center justify-between gap-x-3 gap-y-1 rounded-lg px-4 py-3 text-left transition-colors hover:bg-[#1b2838]"
      >
        <span className="flex items-center gap-2 text-sm font-semibold text-[#e8eef3]">
          <Play className="h-4 w-4 text-[#66c0f4]" aria-hidden />
          Try it
        </span>
        <span className="text-xs text-[#8f98a0]">
          Live request with your key · {unitsLabel(endpoint.units)}
        </span>
      </button>

      {open && (
        <div id={panelId} className="space-y-4 border-t border-[#2a475e]/70 px-4 py-4">
          <div>
            <label htmlFor={`${id}-key`} className="text-xs font-semibold text-[#c7d5e0]">
              API key
            </label>
            <div className="mt-1 flex gap-2">
              <input
                id={`${id}-key`}
                type={showKey ? "text" : "password"}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="spk_…"
                autoComplete="off"
                spellCheck={false}
                data-1p-ignore
                data-lpignore="true"
                className={fieldClass}
              />
              <button
                type="button"
                onClick={() => setShowKey((v) => !v)}
                aria-label={showKey ? "Hide key" : "Show key"}
                className="shrink-0 rounded-md border border-[#2a475e] px-2.5 text-[#8f98a0] transition-colors hover:text-white"
              >
                {showKey ? <EyeOff className="h-4 w-4" aria-hidden /> : <Eye className="h-4 w-4" aria-hidden />}
              </button>
            </div>
            {keyLooksWrong && (
              <p className="mt-1 text-xs text-[#f0a37c]">Keys are {KEY_SHAPE}.</p>
            )}
            <label className="mt-2 flex items-start gap-2 text-xs text-[#98a8b5]">
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                className="mt-0.5 accent-[#66c0f4]"
              />
              <span>
                Remember this key on this device. It is kept in this browser&apos;s storage until you untick the box,
                so leave it off on a shared computer.
              </span>
            </label>
            <p className="mt-1 text-xs text-[#8f98a0]">
              No key yet?{" "}
              <Link href={endpoint.dashboardPath} className={LINK}>
                Create one on the API dashboard
              </Link>
              .
            </p>
          </div>

          {endpoint.params.length > 0 && (
            <fieldset>
              <legend className="text-xs font-semibold text-[#c7d5e0]">Parameters</legend>
              <div className="mt-2 grid gap-3 sm:grid-cols-2">
                {endpoint.params.map((p) => {
                  const inputId = `${id}-p-${p.name}`;
                  const value = values[p.name] ?? "";
                  const set = (v: string) => setValues((prev) => ({ ...prev, [p.name]: v }));
                  const options = p.enumValues ?? (p.type === "boolean" ? ["true", "false"] : null);
                  return (
                    <div key={p.name} className="min-w-0">
                      <div className="flex items-baseline gap-2">
                        <label htmlFor={inputId} className={`${MONO} text-[12px] text-[#e8eef3]`}>
                          {p.name}
                        </label>
                        <span className="text-[10px] uppercase tracking-wider text-[#8f98a0]">
                          {p.in}
                          {p.required ? " · required" : ""}
                        </span>
                      </div>
                      {options ? (
                        <select
                          id={inputId}
                          value={value}
                          onChange={(e) => set(e.target.value)}
                          className={`${fieldClass} mt-1`}
                        >
                          {!p.required && (
                            <option value="">{p.defaultValue ? `default (${p.defaultValue})` : "not set"}</option>
                          )}
                          {options.map((o) => (
                            <option key={o} value={o}>
                              {o}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          id={inputId}
                          type="text"
                          inputMode={p.type === "integer" ? "numeric" : undefined}
                          value={value}
                          onChange={(e) => set(e.target.value)}
                          placeholder={p.defaultValue ? `default: ${p.defaultValue}` : p.required ? "required" : "optional"}
                          spellCheck={false}
                          autoComplete="off"
                          className={`${fieldClass} mt-1`}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </fieldset>
          )}

          <div className="min-w-0">
            <p className="text-xs font-semibold text-[#c7d5e0]">Request</p>
            <p
              className={`${MONO} mt-1 overflow-x-auto whitespace-nowrap rounded-md border border-[#2a475e]/70 bg-[#0e1620] px-2.5 py-1.5 text-[12px] text-[#c7d5e0]`}
              data-testid="try-it-request"
            >
              <span className="font-bold text-[#a4d007]">GET</span> {path}
            </p>
          </div>

          <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center">
            <button
              type="button"
              onClick={send}
              disabled={pending}
              className="inline-flex items-center gap-2 rounded-lg bg-[#66c0f4] px-4 py-2 text-sm font-semibold text-[#0d151e] transition-colors hover:brightness-110 disabled:cursor-wait disabled:opacity-70"
            >
              {pending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Send className="h-4 w-4" aria-hidden />}
              {pending ? "Sending…" : "Send request"}
            </button>
            <p className="min-w-0 flex-1 text-xs leading-relaxed text-[#8f98a0]">
              A real call from this browser, with the key in the <C>Authorization</C> header. {cost}
            </p>
          </div>

          {problem && (
            <p role="alert" className="flex items-start gap-2 rounded-md border border-[#e35e1c]/40 bg-[#e35e1c]/10 px-3 py-2 text-xs text-[#f0c3a8]">
              <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
              {problem}
            </p>
          )}

          {result && (
            <div className="space-y-3" aria-live="polite">
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className={`${MONO} rounded-md border px-2 py-0.5 text-xs font-semibold ${statusTone(result.status)}`}
                  data-testid="try-it-status"
                >
                  {result.status} {result.statusText}
                </span>
                <span className="text-xs text-[#8f98a0]">{result.ms} ms</span>
                {result.errorCode && (
                  <a href={`#${errorAnchor(result.errorCode)}`} className={`${LINK} text-xs`}>
                    What <code className={MONO}>{result.errorCode}</code> means
                  </a>
                )}
              </div>
              <dl className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {(
                  [
                    // Error responses carry no units headers: they cost nothing.
                    ["Units charged", result.headers["x-units-charged"] ?? (result.status >= 400 ? "0" : undefined)],
                    ["Left today", result.headers["x-units-remaining-day"]],
                    ["Left this month", result.headers["x-units-remaining-month"]],
                    ["Requests left this minute", result.headers["x-ratelimit-remaining"]],
                  ] as [string, string | undefined][]
                ).map(([label, v]) => (
                  <div key={label} className="rounded-md border border-[#2a475e]/60 bg-[#131c28] px-2.5 py-1.5">
                    <dt className="text-[10px] uppercase tracking-wider text-[#8f98a0]">{label}</dt>
                    <dd className={`${MONO} text-sm text-[#e8eef3]`}>{numberOrDash(v)}</dd>
                  </div>
                ))}
              </dl>
              {(result.headers["x-request-id"] || result.headers["retry-after"]) && (
                <p className="text-[11px] text-[#8f98a0]">
                  {result.headers["retry-after"] && <>Retry after {result.headers["retry-after"]} s. </>}
                  {result.headers["x-request-id"] && (
                    <>
                      Request id <span className={`${MONO} [overflow-wrap:anywhere]`}>{result.headers["x-request-id"]}</span>
                    </>
                  )}
                </p>
              )}
              <CodeBlock code={result.body} language={result.isJson ? "json" : "text"} title="Response body" collapseAfter={30} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
